/**
 * Reusable component for displaying field-level differences between two objects.
 *
 * This component is designed to work with optimistic locking conflicts,
 * showing users exactly what fields have changed between their local version
 * and the latest server version.
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Plus,
  Minus,
  FileText,
  Calendar,
  Hash,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { renderDateString } from "@/lib/utils";

export interface FieldDiff {
  field: string;
  label: string;
  localValue: any;
  serverValue: any;
  hasChanged: boolean;
  type:
    | "text"
    | "date"
    | "datetime"
    | "number"
    | "boolean"
    | "file"
    | "array"
    | "object";
}

export interface FieldDiffViewerProps {
  /** Array of field differences to display */
  diffs: FieldDiff[];

  /** Title for the diff section */
  title?: string;

  /** Whether to show only changed fields */
  showOnlyChanged?: boolean;

  /** Custom field labels mapping */
  fieldLabels?: Record<string, string>;

  /** Custom field type icons */
  fieldIcons?: Record<string, React.ReactNode>;

  /** Whether the component is in a loading state */
  loading?: boolean;

  /** Custom styling */
  className?: string;
}

const defaultFieldIcons: Record<string, React.ReactNode> = {
  title: <FileText className="h-4 w-4" />,
  description: <FileText className="h-4 w-4" />,
  start_date: <Calendar className="h-4 w-4" />,
  end_date: <Calendar className="h-4 w-4" />,
  total_pages: <Hash className="h-4 w-4" />,
  requires_text_extraction: <CheckCircle2 className="h-4 w-4" />,
  use_bates_numbers: <CheckCircle2 className="h-4 w-4" />,
  file: <FileText className="h-4 w-4" />,
  institution: <FileText className="h-4 w-4" />,
  type: <FileText className="h-4 w-4" />,
};

export function FieldDiffViewer({
  diffs,
  title,
  showOnlyChanged = true,
  fieldLabels = {},
  fieldIcons = {},
  loading = false,
  className = "",
}: FieldDiffViewerProps) {
  const { t } = useTranslation();

  // Filter to show only changed fields if requested
  const displayDiffs = showOnlyChanged
    ? diffs.filter((diff) => diff.hasChanged)
    : diffs;

  // Get field label with fallback
  const getFieldLabel = (field: string) => {
    // If fieldLabels are provided, use them (these are already translated)
    if (fieldLabels[field]) {
      return fieldLabels[field];
    }

    // If no fieldLabels provided, try to translate the field name directly
    // This handles cases where the field name itself is a translation key
    const translationKey = field;
    const translated = t(translationKey);

    // If translation exists and is different from the key, use it
    if (translated !== translationKey) {
      return translated;
    }

    // Fallback to formatted field name
    return field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Get field icon with fallback
  const getFieldIcon = (field: string) => {
    return (
      fieldIcons[field] ||
      defaultFieldIcons[field] || <FileText className="h-4 w-4" />
    );
  };

  // Format value based on type
  const formatValue = (value: any, type: FieldDiff["type"]) => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">—</span>;
    }

    switch (type) {
      case "date":
        if (typeof value === "string") {
          return <span className="text-xs">{renderDateString(value)}</span>;
        }
        if (value instanceof Date) {
          return <span className="text-xs">{value.toLocaleDateString()}</span>;
        }
        return <span className="text-xs">{String(value)}</span>;

      case "datetime":
        if (typeof value === "string") {
          try {
            const date = new Date(value);
            return <span className="text-xs">{date.toLocaleString()}</span>;
          } catch {
            return <span className="text-xs">{String(value)}</span>;
          }
        }
        if (value instanceof Date) {
          return <span className="text-xs">{value.toLocaleString()}</span>;
        }
        return <span className="text-xs">{String(value)}</span>;

      case "boolean":
        return (
          <div className="flex items-center gap-1">
            {value ? (
              <CheckCircle2 className="h-3 w-3 text-green-600" />
            ) : (
              <XCircle className="h-3 w-3 text-red-600" />
            )}
            <span className="text-xs">
              {value ? t("common.label.yes") : t("common.label.no")}
            </span>
          </div>
        );

      case "file":
        if (typeof value === "string") {
          const fileName = value.split("/").pop() || value;
          return (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-xs"
              title={fileName}
            >
              {fileName.length > 20
                ? `${fileName.substring(0, 20)}...`
                : fileName}
            </a>
          );
        }
        if (value instanceof File) {
          const fileName = value.name || t("common.label.untitled_file");
          return (
            <span className="text-xs" title={fileName}>
              {fileName.length > 20
                ? `${fileName.substring(0, 20)}...`
                : fileName}
            </span>
          );
        }
        return <span className="text-xs">{String(value)}</span>;

      case "array":
        if (Array.isArray(value)) {
          const text =
            value.length > 0 ? value.join(", ") : t("common.label.none");
          return (
            <span className="text-xs" title={text}>
              {text.length > 30 ? `${text.substring(0, 30)}...` : text}
            </span>
          );
        }
        return <span className="text-xs">{String(value)}</span>;

      case "object":
        return (
          <span className="text-xs font-mono">
            {JSON.stringify(value).substring(0, 50)}...
          </span>
        );

      default:
        const text = String(value);
        return (
          <span className="text-xs" title={text}>
            {text.length > 30 ? `${text.substring(0, 30)}...` : text}
          </span>
        );
    }
  };

  // Determine change type and styling
  const getChangeType = (diff: FieldDiff) => {
    if (!diff.hasChanged) return "unchanged";

    const hasLocal =
      diff.localValue !== null &&
      diff.localValue !== undefined &&
      diff.localValue !== "";
    const hasServer =
      diff.serverValue !== null &&
      diff.serverValue !== undefined &&
      diff.serverValue !== "";

    if (hasLocal && !hasServer) return "added";
    if (!hasLocal && hasServer) return "removed";
    return "modified";
  };

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case "added":
        return <Plus className="h-4 w-4 text-green-600" />;
      case "removed":
        return <Minus className="h-4 w-4 text-red-600" />;
      case "modified":
        return <ArrowRight className="h-4 w-4 text-blue-600" />;
      default:
        return null;
    }
  };

  const getChangeBadgeVariant = (changeType: string) => {
    switch (changeType) {
      case "added":
        return "default" as const;
      case "removed":
        return "destructive" as const;
      case "modified":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            {title || t("common.title.field_differences")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (displayDiffs.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            {title || t("common.title.field_differences")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-center py-4 text-sm">
            {showOnlyChanged
              ? t("common.statictext.no_field_changes")
              : t("common.statictext.no_fields_to_compare")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          {title || t("common.title.field_differences")}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="h-8">
                <TableHead className="w-8 px-2 py-1"></TableHead>
                <TableHead className="px-2 py-1 font-medium">
                  {t("common.label.field")}
                </TableHead>
                <TableHead className="px-2 py-1 font-medium text-blue-600">
                  {t("common.label.your_version")}
                </TableHead>
                <TableHead className="px-2 py-1 font-medium text-green-600">
                  {t("common.label.current_version")}
                </TableHead>
                <TableHead className="w-20 px-2 py-1 text-center font-medium">
                  {t("common.label.status")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayDiffs.map((diff) => {
                const changeType = getChangeType(diff);
                const changeIcon = getChangeIcon(changeType);
                const badgeVariant = getChangeBadgeVariant(changeType);

                return (
                  <TableRow key={diff.field} className="h-10 hover:bg-muted/50">
                    <TableCell className="px-2 py-1">
                      <div className="flex items-center justify-center">
                        {getFieldIcon(diff.field)}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-sm">
                          {getFieldLabel(diff.field)}
                        </span>
                        {changeIcon}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <div className="text-sm max-w-xs truncate">
                        {formatValue(diff.localValue, diff.type)}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-1">
                      <div className="text-sm max-w-xs truncate">
                        {formatValue(diff.serverValue, diff.type)}
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-1 text-center">
                      <Badge variant={badgeVariant} className="text-xs">
                        {changeType === "added" && t("common.label.added")}
                        {changeType === "removed" && t("common.label.removed")}
                        {changeType === "modified" &&
                          t("common.label.modified")}
                        {changeType === "unchanged" &&
                          t("common.label.unchanged")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
